import './nav-sheet.scss';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { NavigationProps } from '@sola/schema/lib/schema.js';

import { Dialpad } from '../../atoms/dialpad/dialpad';
import { LinkList } from '../../atoms/link-list/link-list';

type NavSheetProps = {
  items: NavigationProps[];
};

type TitleElementProps = {
  children: React.ReactNode;
  className?: string;
  href?: string;
  target?: string;
  useLink?: boolean;
};

const TitleElement = ({
  children,
  className,
  href,
  target,
  useLink,
}: TitleElementProps) => {
  if (useLink) {
    return (
      <Link href={href || '#'} target={target} className={className}>
        {children}
      </Link>
    );
  }
  return <div className={className}>{children}</div>;
};

export const NavSheet: React.FC<NavSheetProps> = ({ items }) => {
  const columnHasIcon = items.some((i) => Boolean(i.icon));

  return (
    <div
      key={items.map((i) => i.title).join()}
      className="NavSheet__parent-container flex flex-col gap-comfy-sm"
    >
      {items.map((item) => {
        const titleHasLink = Boolean(item.link);

        return (
          <div className="flex flex-col" key={item.title}>
            <TitleElement
              useLink={titleHasLink}
              href={item?.link}
              target={item?.target}
              className={cn(
                'label-neutral flex flex-col py-snug-xl px-comfy-xs',
                { NavSheet__link: titleHasLink },
              )}
            >
              <div className="flex gap-snug-xl">
                {item?.icon && (
                  <ArkIcon iconName={item?.icon} className="NavSheet__icon" />
                )}
                <div
                  className={cn('flex flex-col', {
                    'NavSheet__title--padded': columnHasIcon && !item?.icon,
                    'gap-snug-md': item.description,
                  })}
                >
                  <div className="flex gap-snug-xl items-center">
                    <span className="NavSheet__text label label-neutral label-lg label-potent">
                      {item.title}
                    </span>
                    {item.badge?.text && (
                      <Badge
                        label={item.badge?.text}
                        color={item.badge?.color}
                        size="sm"
                      />
                    )}
                  </div>
                  <p className="NavSheet__description label-md label-moderate">
                    {item.description}
                  </p>
                </div>
              </div>
            </TitleElement>

            {/* items that appear below the main nav item */}
            <div
              className={cn({
                'NavSheet__grid-container mt-snug-md':
                  item.displayVariant === 'grid',
              })}
            >
              {Boolean(item?.items?.length) &&
                (item.displayVariant === 'grid' ? (
                  <Dialpad item={item} />
                ) : (
                  <LinkList item={item} useHover usePadding />
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
